import React, { useCallback, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Download, Upload } from 'lucide-react';
import { BULK_STOCKTAKE_MAX_ROWS } from '@threadheads/shared';
import { useStocktakeCsvParser, type StocktakeCsvRow } from '../../../hooks/useCsvParser';
import { useStocktakeExport } from '../../../hooks/useStocktakeExport';
import { translateCsvParseError } from '../../../utils/csvErrorMessages';
import { useBulkStocktakeUpload } from '../useBulkStocktakeUpload';

const UploadStep: React.FC = () => {
  const { t } = useTranslation('inventory');
  const { setFile, setParsedRows, setRawRows, setSourceRows, setStep } = useBulkStocktakeUpload();
  const { exportExample } = useStocktakeExport();

  const {
    file,
    parsedData,
    parseErrors,
    structuredErrors,
    rawRows,
    sourceRows,
    isParsing,
    fileInputRef,
    handleFileChange: baseHandleFileChange,
    resetParser,
    triggerFileSelect,
  } = useStocktakeCsvParser();

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      // Let the parser do its thing first
      await baseHandleFileChange(e);
    },
    [baseHandleFileChange]
  );

  // Raw stock cell text decides COUNTED vs not — `stock: NaN` alone can't
  // tell a blank cell (not counted) from junk like "n/a" (an error).
  const countedCount = rawRows.filter((r) => (r.stock ?? '').trim() !== '').length;

  const isParsedReady = parsedData.length > 0 && parseErrors.length === 0 && !!file;
  const isOverLimit = isParsedReady && countedCount > BULK_STOCKTAKE_MAX_ROWS;
  const isAllBlank = isParsedReady && countedCount === 0;

  // When parsedData arrives with no errors, advance to validation — unless
  // it's blocked by the row-limit or zero-counted guards below.
  const hasAdvanced = useRef(false);
  const reportedOverLimitRef = useRef<StocktakeCsvRow[] | null>(null);

  React.useEffect(() => {
    if (hasAdvanced.current) return;
    if (!isParsedReady || !file) return;

    if (isOverLimit) {
      if (reportedOverLimitRef.current !== parsedData) {
        reportedOverLimitRef.current = parsedData;
        Sentry.captureMessage('Stocktake CSV exceeds row limit', {
          level: 'warning',
          tags: { component: 'UploadStep', operation: 'validateRowLimit' },
          extra: { countedCount, limit: BULK_STOCKTAKE_MAX_ROWS, fileName: file.name },
        });
      }
      return;
    }

    if (isAllBlank) return;

    hasAdvanced.current = true;
    setFile(file);
    setParsedRows(parsedData);
    setRawRows(rawRows);
    setSourceRows(sourceRows);
    setStep('validation');
  }, [
    isParsedReady,
    isOverLimit,
    isAllBlank,
    parsedData,
    rawRows,
    sourceRows,
    file,
    countedCount,
    setFile,
    setParsedRows,
    setRawRows,
    setSourceRows,
    setStep,
  ]);

  const handleDownloadTemplate = () => {
    exportExample();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (!droppedFile) return;

      // Set files on the input and call the change handler with a
      // synthetic React-compatible event so the parser hook processes
      // the dropped file the same way as a manual file selection.
      const dt = new DataTransfer();
      dt.items.add(droppedFile);

      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
        const syntheticEvent = {
          target: fileInputRef.current,
        } as React.ChangeEvent<HTMLInputElement>;
        baseHandleFileChange(syntheticEvent);
      }
    },
    [fileInputRef, baseHandleFileChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  return (
    <div className="space-y-4">
      {/* Warning Alert */}
      <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30">
        <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
        <AlertDescription className="text-orange-800 dark:text-orange-300">
          <Trans
            t={t}
            i18nKey="bulkStocktake.warning"
            components={{ strong: <span className="font-medium" /> }}
          />
        </AlertDescription>
      </Alert>

      {/* Parse Errors — rendered from the STRUCTURED codes so they translate.
          `parseErrors` (English strings) is only the fallback for codes that
          have no dedicated key yet. */}
      {parseErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium mb-1">{t('bulkStocktake.csvErrors')}</div>
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {parseErrors.slice(0, 5).map((error, i) => (
                <li key={i}>
                  {structuredErrors[i]
                    ? translateCsvParseError(structuredErrors[i], t, error)
                    : error}
                </li>
              ))}
              {parseErrors.length > 5 && (
                <li>{t('bulkStocktake.moreErrors', { count: parseErrors.length - 5 })}</li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Row-limit / all-blank blocks — file parsed cleanly but cannot
          advance past this step. */}
      {isOverLimit && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('bulkStocktake.err.overRowLimit', { count: countedCount, limit: BULK_STOCKTAKE_MAX_ROWS })}
          </AlertDescription>
        </Alert>
      )}

      {isAllBlank && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t('bulkStocktake.err.noCountedRows')}</AlertDescription>
        </Alert>
      )}

      {/* Template Download */}
      <div className="space-y-2">
        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="w-full justify-start"
        >
          <Download className="h-4 w-4 mr-2" />
          {t('bulkStocktake.downloadTemplate')}
        </Button>
        <p className="text-xs font-medium text-muted-foreground">{t('bulkStocktake.csvFormat')}</p>
        <p className="text-xs text-muted-foreground">{t('bulkStocktake.templateDesc')}</p>
      </div>

      {/* Drag & Drop Zone */}
      <div
        className="border-2 border-dashed rounded-md p-8 text-center hover:border-emerald-600/40 transition-colors"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            <label
              htmlFor="stocktake-csv-upload"
              className="font-medium text-primary hover:underline cursor-pointer"
            >
              {t('bulkStocktake.dropHere')}
            </label>
          </div>
          <input
            ref={fileInputRef}
            id="stocktake-csv-upload"
            type="file"
            className="hidden"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isParsing}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={triggerFileSelect}
            disabled={isParsing}
          >
            {isParsing ? 'Processing...' : 'Select File'}
          </Button>
          <p className="text-xs text-muted-foreground">{t('bulkStocktake.csvOnly')}</p>
          {file && parseErrors.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Selected: {file.name}
            </p>
          )}
        </div>
      </div>

      {/* If there are parse errors or a blocking guard fired, allow retry */}
      {(parseErrors.length > 0 || isOverLimit || isAllBlank) && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={resetParser}>
            Clear &amp; Try Again
          </Button>
        </div>
      )}
    </div>
  );
};

export default UploadStep;
